import apiClient from "../services/apiClient";

// Service de recherche d'entreprises locales avec qualification économique
export class BusinessFinder {
  constructor() {
    this.apiKeys = {
      googlePlaces: process.env.REACT_APP_GOOGLE_PLACES_API_KEY,
      pagesJaunes: process.env.REACT_APP_PAGES_JAUNES_API_KEY,
      pappers: process.env.REACT_APP_PAPPERS_API_KEY,
      societeCom: process.env.REACT_APP_SOCIETE_COM_API_KEY
    };
  }

  // Recherche d'entreprises par localisation et type
  async searchBusinesses(params) {
    try {
      const data = await apiClient.adminProspectionSearch(params);
      return Array.isArray(data?.results) ? data.results : [];
    } catch (error) {
      console.error("Erreur lors de la recherche d'entreprises:", error);
      throw error;
    }
  }

  // Géocodage d'adresse
  async geocodeAddress(address) {
    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(address)}&key=${this.apiKeys.googlePlaces}`
      );
      const data = await response.json();
      
      if (data.results && data.results.length > 0) {
        const location = data.results[0].geometry.location;
        return { lat: location.lat, lng: location.lng };
      }
      
      throw new Error('Adresse non trouvée');
    } catch (error) {
      console.warn('Géocodage échoué, utilisation de coordonnées par défaut');
      return { lat: 48.8566, lng: 2.3522 }; // Paris par défaut
    }
  }

  // Recherche Google Places
  async searchGooglePlaces(coordinates, radius, businessType) {
    if (!this.apiKeys.googlePlaces) {
      console.warn('Clé API Google Places manquante');
      return [];
    }

    try {
      const typeMapping = this.getGooglePlacesType(businessType);
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/place/nearbysearch/json?` +
        `location=${coordinates.lat},${coordinates.lng}&radius=${radius * 1000}&type=${typeMapping}&key=${this.apiKeys.googlePlaces}`
      );
      
      const data = await response.json();
      
      return data.results.map(place => ({
        id: place.place_id,
        name: place.name,
        address: place.vicinity,
        phone: place.formatted_phone_number || '',
        website: place.website || '',
        rating: place.rating || 0,
        reviewCount: place.user_ratings_total || 0,
        coordinates: {
          lat: place.geometry.location.lat,
          lng: place.geometry.location.lng
        },
        source: 'google_places',
        type: businessType,
        businessPotential: 'unknown' // Sera qualifié plus tard
      }));
    } catch (error) {
      console.error('Erreur Google Places:', error);
      return [];
    }
  }

  // Recherche Pages Jaunes
  async searchPagesJaunes(coordinates, radius, businessType) {
    try {
      const categoryMapping = this.getPagesJaunesCategory(businessType);
      const response = await fetch(
        `https://api.pagesjaunes.fr/v1/search?` +
        `lat=${coordinates.lat}&lng=${coordinates.lng}&radius=${radius}&category=${categoryMapping}`,
        {
          headers: {
            'Authorization': `Bearer ${this.apiKeys.pagesJaunes}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      const data = await response.json();
      
      return data.results.map(business => ({
        id: business.id,
        name: business.name,
        address: business.address,
        phone: business.phone || '',
        website: business.website || '',
        rating: business.rating || 0,
        reviewCount: business.review_count || 0,
        coordinates: {
          lat: business.lat,
          lng: business.lng
        },
        source: 'pages_jaunes',
        type: businessType,
        businessPotential: 'unknown'
      }));
    } catch (error) {
      console.error('Erreur Pages Jaunes:', error);
      return [];
    }
  }

  // Recherche sources locales (simulation pour l'instant)
  async searchLocalSources(coordinates, radius, businessType) {
    // Simulation de données locales
    const localData = this.getLocalBusinessData(businessType);
    
    return localData.map(business => ({
      ...business,
      coordinates: {
        lat: coordinates.lat + (Math.random() - 0.5) * 0.01,
        lng: coordinates.lng + (Math.random() - 0.5) * 0.01
      },
      source: 'local_data',
      businessPotential: 'unknown'
    }));
  }

  // Qualification économique des entreprises
  async qualifyBusinesses(businesses) {
    const qualifiedBusinesses = [];
    
    for (const business of businesses) {
      try {
        const qualification = await this.qualifyBusiness(business);
        qualifiedBusinesses.push({
          ...business,
          ...qualification
        });
      } catch (error) {
        console.warn(`Erreur qualification ${business.name}:`, error);
        qualifiedBusinesses.push({
          ...business,
          businessPotential: 'unknown',
          financialData: null,
          companyInfo: null
        });
      }
    }
    
    return qualifiedBusinesses;
  }

  // Qualification d'une entreprise spécifique
  async qualifyBusiness(business) {
    const qualification = {
      businessPotential: 'low',
      financialData: null,
      companyInfo: null,
      qualificationScore: 0
    };

    try {
      // Recherche sur Pappers
      const pappersData = await this.searchPappers(business.name, business.address);
      if (pappersData) {
        qualification.companyInfo = pappersData;
        qualification.financialData = pappersData.financials;
        qualification.qualificationScore += this.calculatePappersScore(pappersData);
      }

      // Recherche sur Societe.com
      const societeData = await this.searchSocieteCom(business.name, business.address);
      if (societeData) {
        qualification.companyInfo = { ...qualification.companyInfo, ...societeData };
        qualification.qualificationScore += this.calculateSocieteScore(societeData);
      }

      // Analyse du site web (si disponible)
      if (business.website) {
        const websiteAnalysis = await this.analyzeWebsitePotential(business.website);
        qualification.websiteAnalysis = websiteAnalysis;
        qualification.qualificationScore += this.calculateWebsiteScore(websiteAnalysis);
      }

      // Calcul du potentiel final
      qualification.businessPotential = this.calculateBusinessPotential(qualification.qualificationScore);
      
      return qualification;
    } catch (error) {
      console.error('Erreur qualification:', error);
      return qualification;
    }
  }

  // Recherche sur Pappers
  async searchPappers(companyName, address) {
    if (!this.apiKeys.pappers) return null;

    try {
      const response = await fetch(
        `https://api.pappers.fr/v2/entreprises?nom=${encodeURIComponent(companyName)}&adresse=${encodeURIComponent(address)}`,
        {
          headers: {
            'Authorization': `Bearer ${this.apiKeys.pappers}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      const data = await response.json();
      
      if (data.entreprises && data.entreprises.length > 0) {
        const company = data.entreprises[0];
        return {
          siret: company.siret,
          siren: company.siren,
          forme_juridique: company.forme_juridique,
          date_creation: company.date_creation,
          effectif: company.effectif,
          chiffre_affaires: company.chiffre_affaires,
          resultat: company.resultat,
          capital: company.capital,
          dirigeants: company.dirigeants,
          adresse: company.adresse,
          code_naf: company.code_naf,
          libelle_code_naf: company.libelle_code_naf
        };
      }
      
      return null;
    } catch (error) {
      console.error('Erreur Pappers:', error);
      return null;
    }
  }

  // Recherche sur Societe.com
  async searchSocieteCom(companyName, address) {
    if (!this.apiKeys.societeCom) return null;

    try {
      const response = await fetch(
        `https://api.societe.com/v1/search?q=${encodeURIComponent(companyName)}&address=${encodeURIComponent(address)}`,
        {
          headers: {
            'Authorization': `Bearer ${this.apiKeys.societeCom}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      const data = await response.json();
      
      if (data.results && data.results.length > 0) {
        const company = data.results[0];
        return {
          siret: company.siret,
          siren: company.siren,
          forme_juridique: company.forme_juridique,
          date_creation: company.date_creation,
          effectif: company.effectif,
          chiffre_affaires: company.chiffre_affaires,
          resultat: company.resultat,
          capital: company.capital,
          dirigeants: company.dirigeants,
          adresse: company.adresse,
          code_naf: company.code_naf,
          libelle_code_naf: company.libelle_code_naf
        };
      }
      
      return null;
    } catch (error) {
      console.error('Erreur Societe.com:', error);
      return null;
    }
  }

  // Analyse du potentiel du site web
  async analyzeWebsitePotential(website) {
    try {
      const response = await fetch(`https://api.example.com/website-analysis?url=${encodeURIComponent(website)}`);
      const data = await response.json();
      
      return {
        hasWebsite: true,
        isModern: data.isModern || false,
        hasEcommerce: data.hasEcommerce || false,
        hasBlog: data.hasBlog || false,
        hasContactForm: data.hasContactForm || false,
        lastUpdate: data.lastUpdate || null,
        technologies: data.technologies || [],
        estimatedBudget: this.estimateWebsiteBudget(data)
      };
    } catch (error) {
      console.error('Erreur analyse site web:', error);
      return {
        hasWebsite: true,
        isModern: false,
        hasEcommerce: false,
        hasBlog: false,
        hasContactForm: false,
        lastUpdate: null,
        technologies: [],
        estimatedBudget: 'low'
      };
    }
  }

  // Calcul du score Pappers
  calculatePappersScore(pappersData) {
    let score = 0;
    
    if (pappersData.chiffre_affaires) {
      const ca = parseInt(pappersData.chiffre_affaires);
      if (ca > 1000000) score += 30;
      else if (ca > 500000) score += 20;
      else if (ca > 100000) score += 10;
    }
    
    if (pappersData.effectif) {
      const effectif = parseInt(pappersData.effectif);
      if (effectif > 50) score += 20;
      else if (effectif > 10) score += 15;
      else if (effectif > 5) score += 10;
    }
    
    if (pappersData.capital) {
      const capital = parseInt(pappersData.capital);
      if (capital > 100000) score += 15;
      else if (capital > 50000) score += 10;
      else if (capital > 10000) score += 5;
    }
    
    return score;
  }

  // Calcul du score Societe.com
  calculateSocieteScore(societeData) {
    let score = 0;
    
    // Logique similaire à Pappers
    if (societeData.chiffre_affaires) {
      const ca = parseInt(societeData.chiffre_affaires);
      if (ca > 1000000) score += 25;
      else if (ca > 500000) score += 15;
      else if (ca > 100000) score += 8;
    }
    
    return score;
  }

  // Calcul du score site web
  calculateWebsiteScore(websiteAnalysis) {
    let score = 0;
    
    if (websiteAnalysis.isModern) score += 10;
    if (websiteAnalysis.hasEcommerce) score += 15;
    if (websiteAnalysis.hasBlog) score += 5;
    if (websiteAnalysis.hasContactForm) score += 5;
    
    // Score basé sur les technologies
    const modernTechs = ['react', 'vue', 'angular', 'next.js', 'nuxt.js'];
    const hasModernTech = websiteAnalysis.technologies.some(tech => 
      modernTechs.some(modern => tech.toLowerCase().includes(modern))
    );
    
    if (hasModernTech) score += 10;
    
    return score;
  }

  // Calcul du potentiel business final
  calculateBusinessPotential(totalScore) {
    if (totalScore >= 80) return 'high';
    else if (totalScore >= 50) return 'medium';
    else return 'low';
  }

  // Estimation du budget site web
  estimateWebsiteBudget(websiteAnalysis) {
    if (websiteAnalysis.isModern && websiteAnalysis.hasEcommerce) return 'high';
    else if (websiteAnalysis.isModern || websiteAnalysis.hasEcommerce) return 'medium';
    else return 'low';
  }

  // Déduplication des entreprises
  deduplicateBusinesses(businesses) {
    const seen = new Set();
    return businesses.filter(business => {
      const key = `${business.name.toLowerCase()}-${business.address.toLowerCase()}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  // Tri par potentiel
  sortByPotential(businesses) {
    const priorityOrder = { high: 3, medium: 2, low: 1, unknown: 0 };
    
    return businesses.sort((a, b) => {
      const aPriority = priorityOrder[a.businessPotential] || 0;
      const bPriority = priorityOrder[b.businessPotential] || 0;
      
      if (aPriority !== bPriority) return bPriority - aPriority;
      
      // Si même priorité, tri par score de qualification
      return (b.qualificationScore || 0) - (a.qualificationScore || 0);
    });
  }

  // Mapping des types vers Google Places
  getGooglePlacesType(businessType) {
    const mapping = {
      restaurant: 'restaurant',
      avocat: 'lawyer',
      coiffeur: 'hair_care',
      dentiste: 'dentist',
      plombier: 'plumber',
      electricien: 'electrician',
      garage: 'car_repair',
      pharmacie: 'pharmacy',
      boulangerie: 'bakery',
      medecin: 'doctor'
    };
    
    return mapping[businessType] || 'establishment';
  }

  // Mapping des types vers Pages Jaunes
  getPagesJaunesCategory(businessType) {
    const mapping = {
      restaurant: 'restaurants',
      avocat: 'avocats',
      coiffeur: 'coiffeurs',
      dentiste: 'dentistes',
      plombier: 'plombiers',
      electricien: 'electriciens',
      garage: 'garages',
      pharmacie: 'pharmacies',
      boulangerie: 'boulangeries',
      medecin: 'medecins'
    };
    
    return mapping[businessType] || 'entreprises';
  }

  // Données locales (fallback)
  getLocalBusinessData(businessType) {
    const businessData = {
      restaurant: [
        {
          name: 'Le Petit Bistrot',
          address: '123 Rue de la Paix',
          phone: '01 23 45 67 89',
          website: 'https://lepetitbistrot.fr',
          rating: 4.2,
          reviewCount: 156
        }
      ],
      avocat: [
        {
          name: 'Cabinet Maître Dupont',
          address: '10 Rue de la Justice',
          phone: '01 45 67 89 12',
          website: 'https://cabinet-dupont-avocat.fr',
          rating: 4.7,
          reviewCount: 45
        }
      ]
    };

    return businessData[businessType] || [];
  }

  // Export des résultats avec qualification
  exportResults(businesses, format = 'json') {
    switch (format) {
      case 'csv':
        return this.exportToCSV(businesses);
      case 'json':
      default:
        return JSON.stringify(businesses, null, 2);
    }
  }

  // Export CSV avec données de qualification
  exportToCSV(businesses) {
    const headers = [
      'Nom', 'Adresse', 'Téléphone', 'Site web', 'Type', 'Potentiel', 
      'Score qualification', 'Chiffre d\'affaires', 'Effectif', 'Capital',
      'SIRET', 'Date création', 'Forme juridique', 'Budget estimé site web'
    ];
    
    const rows = businesses.map(b => [
      b.name,
      b.address,
      b.phone,
      b.website,
      b.type,
      b.businessPotential,
      b.qualificationScore || 0,
      b.financialData?.chiffre_affaires || '',
      b.financialData?.effectif || '',
      b.financialData?.capital || '',
      b.companyInfo?.siret || '',
      b.companyInfo?.date_creation || '',
      b.companyInfo?.forme_juridique || '',
      b.websiteAnalysis?.estimatedBudget || ''
    ]);
    
    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');
    
    return csvContent;
  }
}

// Instance singleton
export const businessFinder = new BusinessFinder();